import { isAnalyticsGranted } from "../consent";

/**
 * GA4 event helper for the public surfaces (marketing site, startup wizard,
 * playground). The tag itself is loaded by wwwroot/ga4-defer.js (or the
 * inline equivalent in playground.html) with Consent Mode v2 defaulting to
 * denied; this module only queues `gtag('event', …)` commands on the shared
 * dataLayer. Nothing is queued until the visitor has granted analytics
 * consent in the cookie banner — belt-and-suspenders on top of Consent Mode.
 *
 * Event names and parameters follow the "Plano de eventos GA4" document:
 * GA4 recommended names where one exists (generate_lead, begin_checkout,
 * sign_up, purchase), snake_case otherwise.
 */
export type Ga4Params = Record<string, string | number | boolean | undefined | Ga4Item[]>;

export interface Ga4Item {
    item_id: string;
    item_name?: string;
    price?: number;
    quantity?: number;
}

const isBrowser = (): boolean => typeof window !== "undefined";

/** Push a real `arguments` object — gtag.js only recognises those, not arrays. */
const gtag = (...args: unknown[]): void => {
    if (!isBrowser()) return;
    const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...a: unknown[]) => void };
    if (typeof w.gtag === "function") {
        w.gtag(...args);
        return;
    }
    if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push((function () { return arguments; }).apply(null, args as never));
};

/** Send one GA4 event. Undefined parameters are dropped. */
export const track = (name: string, params: Ga4Params = {}): void => {
    if (!isBrowser() || !isAnalyticsGranted()) return;
    const clean: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== "") clean[key] = value;
    }
    gtag("event", name, clean);
};

/**
 * Like `track`, for events fired right before the page unloads (a reload or a
 * hard navigation): the beacon transport lets the request outlive the page.
 */
export const trackBeforeUnload = (name: string, params: Ga4Params = {}): void => {
    track(name, { ...params, transport_type: "beacon" });
};

const CLICK_ATTRIBUTE = "data-ga-event";
let clickTrackingInstalled = false;

/**
 * Declarative click events: any element (or ancestor) carrying
 * `data-ga-event="name"` fires that event on click, with every other
 * `data-ga-*` attribute as a parameter (`data-ga-location="hero"` →
 * `location: "hero"`). Installed once per document.
 */
export const installDeclarativeClickTracking = (): void => {
    if (!isBrowser() || clickTrackingInstalled) return;
    clickTrackingInstalled = true;
    document.addEventListener("click", (event) => {
        const target = event.target as Element | null;
        const el = target?.closest?.(`[${CLICK_ATTRIBUTE}]`) as HTMLElement | null;
        if (!el) return;
        const name = el.getAttribute(CLICK_ATTRIBUTE);
        if (!name) return;
        const params: Ga4Params = {};
        for (const [key, value] of Object.entries(el.dataset)) {
            if (key.startsWith("ga") && key !== "gaEvent" && value !== undefined) {
                params[key.slice(2).toLowerCase()] = value;
            }
        }
        track(name, params);
    }, { capture: true });
};

/**
 * The GA4 client id and session id the tag stored in its first-party cookies
 * (`_ga` → `GA1.1.<client>`, `_ga_<STREAM>` → `GS1.1.<session>…`). Sent with
 * the subscription so the server-side `purchase` (Measurement Protocol)
 * lands on the same visitor and session as the browser-side funnel.
 */
export const readGaIds = (): { clientId?: string; sessionId?: string } => {
    if (!isBrowser() || typeof document === "undefined") return {};
    const cookies = document.cookie.split(";").map((c) => c.trim());
    let clientId: string | undefined;
    let sessionId: string | undefined;
    for (const cookie of cookies) {
        const eq = cookie.indexOf("=");
        if (eq < 0) continue;
        const name = cookie.slice(0, eq);
        const value = decodeURIComponent(cookie.slice(eq + 1));
        if (name === "_ga") {
            // GA1.1.1234567890.1700000000 → 1234567890.1700000000
            const parts = value.split(".");
            if (parts.length >= 4) clientId = `${parts[2]}.${parts[3]}`;
        } else if (name.startsWith("_ga_")) {
            // GS1.1.1700000000.1.1.1700000100.60.0.0 → 1700000000
            const parts = value.split(".");
            if (parts.length >= 3) sessionId = parts[2];
        }
    }
    return { clientId, sessionId };
};
